import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/apiClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('fr_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem('fr_token') || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Keep localStorage in sync whenever token/user changes
  useEffect(() => {
    if (token && user) {
      localStorage.setItem('fr_token', token);
      localStorage.setItem('fr_user', JSON.stringify(user));
    }
  }, [token, user]);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.post('/auth/login', { email, password });
      setToken(res.data.token);
      setUser(res.data.user);
      return { ok: true };
    } catch (err) {
      const msg = err.response?.data?.error || 'Login gagal, coba lagi.';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (username, email, password, spice_preference) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.post('/auth/register', {
        username,
        email,
        password,
        spice_preference,
      });
      setToken(res.data.token);
      setUser(res.data.user);
      return { ok: true };
    } catch (err) {
      const msg = err.response?.data?.error || 'Registrasi gagal, coba lagi.';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSpicePreference = useCallback(async (spice_preference) => {
    try {
      const res = await apiClient.put('/auth/preferences', { spice_preference });
      setUser(prev => ({ ...prev, spice_preference: res.data.user?.spice_preference || spice_preference }));
      return { ok: true };
    } catch (err) {
      const msg = err.response?.data?.error || 'Gagal mengubah tingkat pedas.';
      return { ok: false, error: msg };
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('fr_token');
    localStorage.removeItem('fr_user');
    queryClient.clear();
  }, [queryClient]);

  return (
    <AuthContext.Provider value={{ user, token, loading, error, login, register, logout, updateSpicePreference, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
